import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { useState } from "react";

import ContactSettings from "../components/ContactSettings";
import RegistrationSettings from "../components/RegistrationSettings";
import { adminApiClient } from "../services/apiClient";
import { toApiError } from "../types/api";

import type { ReactElement } from "react";

interface CacheClearingState {
  redis: boolean;
  all: boolean;
}

const SettingsPage = (): ReactElement => {
  const [cacheClearing, setCacheClearing] = useState<CacheClearingState>({
    redis: false,
    all: false,
  });
  const [cacheMessage, setCacheMessage] = useState<string | null>(null);

  const handleClearRedisCache = async (): Promise<void> => {
    setCacheClearing((prev) => ({ ...prev, redis: true }));
    setCacheMessage(null);

    try {
      await adminApiClient.post("/admin/cache/clear-redis");
      setCacheMessage("Redis cache cleared successfully");
    } catch (error: unknown) {
      const apiError = toApiError(error);
      setCacheMessage(apiError.message || "Failed to clear Redis cache");
    } finally {
      setCacheClearing((prev) => ({ ...prev, redis: false }));
    }
  };

  const handleClearAllCache = async (): Promise<void> => {
    if (
      !window.confirm(
        "Are you sure you want to clear ALL cache? This will remove Redis cache data but preserve audio files.",
      )
    ) {
      return;
    }

    setCacheClearing((prev) => ({ ...prev, all: true }));
    setCacheMessage(null);

    try {
      await adminApiClient.post("/admin/cache/clear-all");
      setCacheMessage("All cache cleared successfully");
    } catch (error: unknown) {
      const apiError = toApiError(error);
      setCacheMessage(apiError.message || "Failed to clear cache");
    } finally {
      setCacheClearing((prev) => ({ ...prev, all: false }));
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Settings
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContactSettings />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardHeader title="System Controls" />
            <CardContent>
              <RegistrationSettings />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardHeader title="Cache Management" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Manage system cache. Note: Only Redis cache will be cleared.
                Audio files are permanent storage.
              </Typography>

              {cacheMessage && (
                <Alert
                  severity={
                    cacheMessage.includes("successfully") ? "success" : "error"
                  }
                  sx={{ mb: 2 }}
                >
                  {cacheMessage}
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleClearRedisCache}
                  disabled={cacheClearing.redis}
                  startIcon={
                    cacheClearing.redis ? <CircularProgress size={16} /> : null
                  }
                >
                  Clear Redis Cache
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearAllCache}
                  disabled={cacheClearing.all}
                  startIcon={
                    cacheClearing.all ? <CircularProgress size={16} /> : null
                  }
                >
                  Clear All Cache
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPage;
